// SCR-STAFF-855 (AP-INS-02؛ US-INS-080 تا 082): ثبت واریزی بیمه‌گر با تخصیص خودکار، اصلاح دستی تخصیص و ورود صورت‌حساب
// رسیدگی با نمایه نگاشت ستون‌ها. تراز هر قلم همیشه برقرار است (مطالبه = واریزی + کسورات + سوخت + مانده — BR-INS-080) و
// تخصیص بیش از مانده با PHX-INS-0209 رد می‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsClaimItemView,
  type InsInsurerView,
  type InsRemittanceView,
  type InsSettlementImportProfileView,
  type InsSettlementImportView,
  type InsSettlementUnmatchedRowView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { insuranceKeys } from './common';
import { claimKeys } from './claims';

export const SCR_STAFF_855 = 'SCR-STAFF-855';

export function RemittancesPage() {
  const { t } = useTranslation('ins');
  const access = useAccess();
  const write = useWriteAccess('ins.remittance.manage');
  const { tenantApi, session } = useTenantSession();
  const [tab, setTab] = useState<'remittances' | 'import'>('remittances');
  const [insurerId, setInsurerId] = useState('');

  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers', { params: { query: { limit: 200 } } })),
  });

  return (
    <ListPage
      screenId={SCR_STAFF_855}
      titleKey="ins:remittances.title"
      subtitleKey="ins:remittances.subtitle"
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <TextField
            select
            size="small"
            label={t('ins:remittances.insurer')}
            value={insurerId}
            onChange={(event) => setInsurerId(event.target.value)}
            sx={{ minWidth: 200 }}
            data-testid="ins-remittances-insurer"
          >
            <MenuItem value="">{t('ins:claims.all')}</MenuItem>
            {(insurers.data?.items ?? []).map((insurer: InsInsurerView) => (
              <MenuItem key={insurer.insurerId} value={insurer.insurerId}>
                {insurer.name}
              </MenuItem>
            ))}
          </TextField>
          <Tabs value={tab} onChange={(_, value: 'remittances' | 'import') => setTab(value)}>
            <Tab value="remittances" label={t('ins:remittances.tab_remittances')} data-testid="ins-tab-remittances" />
            <Tab value="import" label={t('ins:remittances.tab_import')} data-testid="ins-tab-import" />
          </Tabs>
        </Stack>
      }
    >
      {tab === 'remittances' ? (
        <RemittancesTab insurerId={insurerId} canManage={access.can('ins.remittance.manage') && write.canWrite} />
      ) : (
        <ImportTab insurerId={insurerId} canImport={access.can('ins.deduction.manage') && write.canWrite} />
      )}
    </ListPage>
  );
}

/** واریزی‌ها با تخصیص خودکار و اصلاح دستی (AC-INS-080.1، AC-INS-081.1). */
function RemittancesTab({ insurerId, canManage }: { insurerId: string; canManage: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const [creating, setCreating] = useState(false);
  const [allocating, setAllocating] = useState<InsRemittanceView | null>(null);

  const remittances = useQuery({
    queryKey: claimKeys.remittances(session?.tenantId, insurerId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/insurance/remittances', {
          params: { query: { insurerId: insurerId === '' ? undefined : insurerId } },
        }),
      ),
  });

  const columns: DataTableColumn<InsRemittanceView>[] = [
    { id: 'insurer', headerKey: 'ins:remittances.columns.insurer', cell: (row) => row.insurerName },
    { id: 'amount', headerKey: 'ins:remittances.columns.amount', cell: (row) => <Money amountMinor={Number(row.amount.amount)} />, align: 'right' },
    { id: 'allocated', headerKey: 'ins:remittances.columns.allocated', cell: (row) => <Money amountMinor={Number(row.allocated.amount)} />, align: 'right' },
    {
      id: 'unallocated',
      headerKey: 'ins:remittances.columns.unallocated',
      cell: (row) =>
        Number(row.unallocated.amount) > 0 ? (
          <span data-testid={`ins-remittance-unallocated-${row.remittanceId}`}>
            <Money amountMinor={Number(row.unallocated.amount)} />
          </span>
        ) : (
          <Money amountMinor={0} />
        ),
      align: 'right',
    },
    { id: 'received_on', headerKey: 'ins:remittances.columns.received_on', cell: (row) => <JalaliDate value={row.receivedOn} /> },
    { id: 'reference', headerKey: 'ins:remittances.columns.reference', cell: (row) => row.reference ?? '—' },
    {
      id: 'allocations',
      headerKey: 'ins:remittances.allocations',
      cell: (row) => (
        <Stack spacing={0.25} data-testid={`ins-allocations-${row.remittanceId}`}>
          {row.allocations.length === 0
            ? '—'
            : row.allocations.map((allocation) => (
                <Typography key={allocation.allocationId} variant="body2">
                  <Money amountMinor={Number(allocation.amount.amount)} />{' '}
                  <StatusChip
                    tone={allocation.isAutomatic ? 'active' : 'pending'}
                    labelKey={allocation.isAutomatic ? 'ins:remittances.automatic' : 'ins:remittances.manual'}
                  />
                </Typography>
              ))}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'ins:remittances.columns.actions',
      cell: (row) =>
        canManage ? (
          <Button size="small" onClick={() => setAllocating(row)} data-testid={`ins-remittance-allocate-${row.remittanceId}`}>
            {t('ins:remittances.allocate')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2}>
      {canManage ? (
        <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => setCreating(true)} data-testid="ins-remittance-create">
          {t('ins:remittances.create')}
        </Button>
      ) : null}
      <DataTable
        captionKey="ins:remittances.caption"
        columns={columns}
        rows={remittances.data}
        getRowId={(row) => row.remittanceId}
        isLoading={remittances.isPending}
        error={remittances.error}
        onRetry={() => void remittances.refetch()}
        emptyTitleKey="ins:remittances.empty"
      />
      {creating && <RemittanceDialog insurerId={insurerId} onClose={() => setCreating(false)} />}
      {allocating && <AllocateDialog remittance={allocating} onClose={() => setAllocating(null)} />}
    </Stack>
  );
}

const remittanceSchema = z.object({
  insurerId: z.string().min(1, 'common:validation.required'),
  amount: z.number().int().positive('ins:remittances.amount'),
  receivedOn: z.string().min(1, 'common:validation.required'),
  reference: z.string().max(64),
});

type RemittanceForm = z.input<typeof remittanceSchema>;

/** ثبت واریزی بیمه‌گر؛ تخصیص خودکار در سرور انجام می‌شود (AC-INS-081.1). */
function RemittanceDialog({ insurerId, onClose }: { insurerId: string; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers', { params: { query: { limit: 200 } } })),
  });
  const form = useForm<RemittanceForm>({
    resolver: zodResolver(remittanceSchema),
    defaultValues: { insurerId, amount: 0, receivedOn: '', reference: '' },
  });

  const save = useMutation({
    mutationFn: (values: RemittanceForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/remittances', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            insurerId: values.insurerId,
            branchId: session?.branchIds[0] ?? '',
            amount: Number(values.amount),
            receivedOn: values.receivedOn,
            bankAccountId: null,
            reference: values.reference === '' ? null : values.reference,
            batchIds: null,
          },
        }),
      ),
    onSuccess: async () => {
      idempotency.settle();
      await queryClient.invalidateQueries({ queryKey: ['ins', 'remittances'] });
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['amount', 'reference', 'insurerId'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_855}
      titleKey="ins:remittances.create_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={form.control}
        name="insurerId"
        labelKey="ins:remittances.insurer"
        options={(insurers.data?.items ?? []).map((insurer: InsInsurerView) => ({
          value: insurer.insurerId,
          label: insurer.name,
        }))}
        fullWidth
      />
      <MoneyFieldRHF control={form.control} name="amount" labelKey="ins:remittances.amount" fullWidth autoFocus />
      <JalaliDatePickerRHF control={form.control} name="receivedOn" labelKey="ins:remittances.received_on" />
      <TextFieldRHF control={form.control} name="reference" labelKey="ins:remittances.reference" fullWidth />
    </FormDialog>
  );
}

const allocateSchema = z.object({
  claimItemId: z.string().min(1, 'common:validation.required'),
  amount: z.number().int().positive('ins:remittances.allocate_amount'),
});

type AllocateForm = z.input<typeof allocateSchema>;

/** اصلاح دستی تخصیص (AC-INS-081.1): تخصیص‌های پیشین برداشته و تخصیص تازه ثبت می‌شود. */
function AllocateDialog({ remittance, onClose }: { remittance: InsRemittanceView; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>();
  const items = useQuery({
    queryKey: claimKeys.items(session?.tenantId, 'submitted'),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/insurance/claim-items', { params: { query: { status: 'Submitted' } } })),
  });
  const form = useForm<AllocateForm>({
    resolver: zodResolver(allocateSchema),
    defaultValues: {
      claimItemId: remittance.allocations[0]?.claimItemId ?? '',
      amount: Number(remittance.amount.amount),
    },
  });

  const save = useMutation({
    mutationFn: (values: AllocateForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/remittances/{id}:allocate', {
          params: { path: { id: remittance.remittanceId } },
          body: { allocations: [{ claimItemId: values.claimItemId, amount: Number(values.amount) }] },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ins', 'remittances'] });
      await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, {
        fields: ['amount', 'claimItemId'],
        codeFields: { 'PHX-INS-0209': 'amount' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      screenId={SCR_STAFF_855}
      titleKey="ins:remittances.allocate_title"
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      onClose={onClose}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={form.control}
        name="claimItemId"
        labelKey="ins:remittances.allocate_item"
        options={(items.data ?? []).map((item: InsClaimItemView) => ({
          value: item.claimItemId,
          label: `${item.patientName} · ${item.serviceName}`,
        }))}
        fullWidth
      />
      <MoneyFieldRHF control={form.control} name="amount" labelKey="ins:remittances.allocate_amount" fullWidth autoFocus />
    </FormDialog>
  );
}

const importSchema = z.object({
  profileId: z.string().min(1, 'common:validation.required'),
  content: z.string().min(1, 'common:validation.required'),
  dryRun: z.boolean(),
});

type ImportForm = z.input<typeof importSchema>;

/** ورود صورت‌حساب رسیدگی با نمایه نگاشت ستون‌ها و فهرست سطرهای بی‌تطبیق (AC-INS-082.1). */
function ImportTab({ insurerId, canImport }: { insurerId: string; canImport: boolean }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [result, setResult] = useState<InsSettlementImportView | null>(null);
  const [submitError, setSubmitError] = useState<unknown>();

  const profiles = useQuery({
    queryKey: claimKeys.profiles(session?.tenantId, insurerId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/insurance/settlement-profiles', {
          params: { query: { insurerId: insurerId === '' ? undefined : insurerId } },
        }),
      ),
  });

  const form = useForm<ImportForm>({
    resolver: zodResolver(importSchema),
    defaultValues: { profileId: '', content: '', dryRun: true },
  });

  const run = useMutation({
    mutationFn: (values: ImportForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/settlement-imports', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            profileId: values.profileId,
            batchId: null,
            content: values.content,
            dryRun: values.dryRun === true,
          },
        }),
      ),
    onSuccess: async (view) => {
      idempotency.settle();
      setResult(view);
      setSubmitError(undefined);

      if (view.dryRun !== true) {
        await queryClient.invalidateQueries({ queryKey: ['ins', 'claim-items'] });
      }
    },
    onError: (error) => {
      idempotency.settle(error);
      const applied = applyServerErrors(error, form.setError, { fields: ['content', 'profileId'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const profileColumns: DataTableColumn<InsSettlementImportProfileView>[] = [
    { id: 'name', headerKey: 'ins:remittances.columns.name', cell: (row) => row.name },
    { id: 'insurer', headerKey: 'ins:remittances.columns.insurer', cell: (row) => row.insurerName },
    { id: 'delimiter', headerKey: 'ins:remittances.columns.delimiter', cell: (row) => row.delimiter },
    { id: 'scale', headerKey: 'ins:remittances.columns.scale', cell: (row) => faDigits(Number(row.amountScale)), align: 'right' },
  ];

  const unmatchedColumns: DataTableColumn<InsSettlementUnmatchedRowView>[] = [
    { id: 'row', headerKey: 'ins:remittances.columns.row', cell: (row) => faDigits(Number(row.rowNumber)), align: 'right' },
    {
      id: 'service_date',
      headerKey: 'ins:remittances.columns.service_date',
      cell: (row) => (row.serviceDate ? <JalaliDate value={row.serviceDate} /> : '—'),
    },
    { id: 'service_code', headerKey: 'ins:remittances.columns.service_code', cell: (row) => row.serviceCode ?? '—' },
    { id: 'approved', headerKey: 'ins:remittances.columns.approved', cell: (row) => <Money amountMinor={Number(row.approved.amount)} />, align: 'right' },
    { id: 'deduction', headerKey: 'ins:remittances.columns.deduction', cell: (row) => <Money amountMinor={Number(row.deduction.amount)} />, align: 'right' },
    { id: 'reason', headerKey: 'ins:remittances.columns.reason', cell: (row) => row.reason },
  ];

  return (
    <Stack spacing={2}>
      <DataTable
        captionKey="ins:remittances.profiles_caption"
        columns={profileColumns}
        rows={profiles.data}
        getRowId={(row) => row.profileId}
        isLoading={profiles.isPending}
        error={profiles.error}
        onRetry={() => void profiles.refetch()}
        emptyTitleKey="ins:remittances.profiles_empty"
      />
      {canImport ? (
        <Stack
          component="form"
          spacing={2}
          noValidate
          onSubmit={form.handleSubmit((values) => run.mutate(values))}
          data-testid="ins-import-form"
        >
          <SelectRHF
            control={form.control}
            name="profileId"
            labelKey="ins:remittances.import_profile"
            options={(profiles.data ?? []).map((profile) => ({ value: profile.profileId, label: profile.name }))}
            fullWidth
          />
          <TextFieldRHF
            control={form.control}
            name="content"
            labelKey="ins:remittances.import_content"
            fullWidth
            multiline
            minRows={4}
          />
          <CheckboxRHF control={form.control} name="dryRun" labelKey="ins:remittances.import_dry_run" />
          <Button type="submit" variant="contained" sx={{ alignSelf: 'flex-start' }} disabled={run.isPending} data-testid="ins-import-run">
            {t('ins:remittances.import')}
          </Button>
        </Stack>
      ) : null}
      {submitError ? <Alert severity="error">{t('common:states.error_title')}</Alert> : null}
      {result ? (
        <Stack spacing={1}>
          <Alert severity={result.dryRun === true ? 'info' : 'success'} data-testid="ins-import-result">
            {t('ins:remittances.import_result', {
              rows: faDigits(Number(result.rows)),
              matched: faDigits(Number(result.matched)),
              deducted: faDigits(Number(result.deductionTotal.amount)),
            })}
            {result.dryRun === true ? ` ${t('ins:remittances.import_preview')}` : ''}
          </Alert>
          <DataTable
            captionKey="ins:remittances.unmatched_caption"
            columns={unmatchedColumns}
            rows={result.unmatched}
            getRowId={(row) => String(row.rowNumber)}
            emptyTitleKey="ins:remittances.unmatched_empty"
          />
        </Stack>
      ) : null}
    </Stack>
  );
}
